import { Dispatch, SetStateAction, useState, type JSX } from 'react';
import Button from '@monofront/design-system/components/Button';
import Modal from '@monofront/design-system/organisms/Modal';
import Label from '@monofront/design-system/components/Form/Label';
import Input from '@monofront/design-system/components/Form/Input';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { EMAIL_REGEX } from '@monofront/business/constants';
import logger from '@utils/logger';
import { useTranslations } from 'next-intl';

interface IModalSingleInputProps {
  modalTitle?: JSX.Element;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  buttonLabel: string;
  cancelButtonLabel: string;
  inputLabel?: string | JSX.Element;
  inputPlaceholder: string;
  submitHandler: (params: Record<string, any>) => Promise<void>;
  toastSuccessMessage: string;
  toastErrorMessage?: string;
  readOnlyInput?: boolean;
  inputDefaultValue?: string;
  hasEmailValidation?: boolean;
  closeModalOnSuccess?: boolean;
  isClosable?: boolean;
}

const ModalSingleInput: React.FC<IModalSingleInputProps> = ({
  modalTitle,
  isModalOpen,
  setIsModalOpen,
  buttonLabel,
  cancelButtonLabel,
  inputLabel,
  inputPlaceholder,
  submitHandler,
  toastSuccessMessage,
  toastErrorMessage,
  readOnlyInput = false,
  inputDefaultValue,
  hasEmailValidation = false,
  closeModalOnSuccess = true,
  isClosable = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const tAccount = useTranslations('Account');

  const { ref: inputRef, ...inputRegistration } = register('inputValue', {
    required: true,
    ...(hasEmailValidation && {
      pattern: {
        value: EMAIL_REGEX,
        message: tAccount('useValidEmail'),
      },
    }),
  });

  const triggerOnSubmit = async (params: Record<string, any>) => {
    try {
      setIsLoading(true);

      await submitHandler(params);

      closeModalOnSuccess && setIsModalOpen(false);
      toast(toastSuccessMessage);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);

      logger.error('Modal input function', { error, toastErrorMessage });

      toast(error?.message || toastErrorMessage, {
        progressClassName: 'toast-error',
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        UNSAFE_className="modal-form"
        externalToggle={() => setIsModalOpen(false)}
        isClosable={isClosable}
        hasPadding
      >
        {modalTitle}
        <form
          className="container-modal-form"
          onSubmit={handleSubmit(triggerOnSubmit)}
        >
          {inputLabel && <Label id="label">{inputLabel}</Label>}
          <Input
            id="label"
            defaultValue={inputDefaultValue}
            placeholder={inputPlaceholder}
            {...inputRegistration}
            register={inputRef}
            readOnly={readOnlyInput}
            dataTestId="ModalSingleInput-Input-1"
          />
          {errors?.inputValue?.message && (
            <p className="error tw-lf-error-message">
              {errors.inputValue.message as string}
            </p>
          )}
          <div className="modal-cta">
            {isClosable && (
              <Button
                variant="secondary"
                hasMinWidth={false}
                onClick={(e) => {
                  e.preventDefault();
                  setIsModalOpen(false);
                }}
                dataTestId="ModalSingleInput-Button-2"
              >
                {cancelButtonLabel}
              </Button>
            )}
            <Button
              type="submit"
              hasMinWidth={false}
              isLoading={isLoading}
              dataTestId="ModalSingleInput-Button-3"
            >
              {buttonLabel}
            </Button>
          </div>
        </form>
      </Modal>
      <style jsx>{`
        :global(.modal-form) {
          top: 33%;
        }

        .container-modal-form > :global(label) {
          margin-bottom: var(--space-2);
        }

        .modal-cta {
          margin-top: var(--space-5);
          display: flex;
          align-items: center;
          justify-content: space-around;
        }

        .modal-cta > :global(button:first-child) {
          margin-right: var(--space-6);
        }

        .modal-cta > :global(button) {
          white-space: nowrap;
        }
      `}</style>
    </>
  );
};

export default ModalSingleInput;
